import { useMutation, useQueryClient } from "@tanstack/react-query";
import { XCircle } from "lucide-react";
import { useEffect, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router";

import { useOrganization } from "@/auth/organization";
import {
  ErrorAlert,
  ErrorState,
  FullPageSpinner,
  Modal,
  cx,
  Badge,
  Section,
  Breadcrumb,
  Button,
  buttonClass,
} from "@/components/ui";
import {
  DeleteEffect,
  PendingDeletionNotice,
  useDeleteSite,
  useDeletionGrace,
} from "@/components/sites/SiteDeletion";
import { portal } from "@/lib/api";
import { formatClock, siteHref } from "@/lib/format";
import { deploymentSteps, isSettled, useHostedSite, useHosting, type StepState } from "@/lib/hosting";
import { primarySite } from "@/lib/queries";
import { DnsInstructions } from "@/pages/websites/WebsiteDetail";
import { HostingIcon, HostingHead, CardHead } from "@/components/hosting/parts";

const TONE = { done: "success", active: "warning", waiting: "neutral", failed: "danger" } as const;
const clock = (iso: string | null) => (iso ? formatClock(Date.parse(iso)) : "—");

function StepIcon({ state }: { state: StepState }) {
  if (state === "done") return <HostingIcon name="check-circle" size={18} />;
  if (state === "active")
    return <HostingIcon name="loading-step" size={18} className="animate-spin [animation-duration:2s]" />;
  if (state === "failed") return <XCircle className="size-[18px] shrink-0 text-[#b8291f]" aria-hidden />;
  return <HostingIcon name="clock" size={18} />;
}

/** Figma "Hosting — Deploying · awaiting DNS & TLS" (HST-01): one external site's progress. */
export function HostedSitePage() {
  const { siteId = "" } = useParams();
  const { orgId } = useOrganization();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const hosted = useHostedSite(siteId);
  const all = useHosting();
  const [showDns, setShowDns] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const domain = hosted.data?.domains[0];
  // When the deployment or domain settles, the overview's copy is out of date.
  const settledNow = hosted.data ? isSettled(hosted.data) : false;
  useEffect(() => {
    if (settledNow) void qc.invalidateQueries({ queryKey: ["hosting", orgId] });
  }, [settledNow, orgId, qc]);
  const recheck = useMutation({
    mutationFn: async () => {
      if (domain && domain.status !== "active") await portal.verifyDomain(domain.id);
    },
    onSettled: () => {
      void hosted.refetch();
      void qc.invalidateQueries({ queryKey: ["hosting", orgId] });
    },
  });
  const grace = useDeletionGrace();
  const remove = useDeleteSite({ id: siteId }, async () => {
    await qc.invalidateQueries({ queryKey: ["hosting"] });
    await qc.invalidateQueries({ queryKey: ["overview"] });
    void navigate("/hosting");
  });

  if (hosted.isPending) return <FullPageSpinner />;
  if (hosted.isError) return <ErrorState error={hosted.error} onRetry={() => void hosted.refetch()} />;
  const { site, deployments } = hosted.data;
  // The Weavex website has its own page.
  if (site.kind !== "external") return <Navigate to={`/websites/${site.id}`} replace />;

  const steps = deploymentSteps(hosted.data, clock);
  const settled = isSettled(hosted.data);
  const live = steps.at(-1)?.state === "done";
  const failed = steps.some((s) => s.state === "failed");
  const latest = deployments[0];
  const address = domain?.hostname ?? site.hostname;
  const scheduled = site.status === "pending_deletion";
  const main = all.data && primarySite(all.data.map((h) => h.site));
  const mainAddress =
    all.data?.find((h) => h.site.id === main?.id)?.domains.find((d) => d.status === "active")?.hostname ??
    main?.hostname;

  return (
    <div className="flex flex-col gap-6">
      <Breadcrumb items={[{ label: "Hosting", to: "/hosting" }, { label: site.name }]} />
      <HostingHead
        title={address}
        status={
          scheduled ? (
            <Badge tone="warning">Scheduled for deletion</Badge>
          ) : live ? (
            <Badge tone="success">Live</Badge>
          ) : failed ? (
            <Badge tone="danger">Needs attention</Badge>
          ) : (
            <Badge tone="warning">Not live yet</Badge>
          )
        }
        subtitle={`External static site${latest ? ` · deployment ${latest.id.slice(0, 8)}` : ""}`}
        actions={
          <>
            {live && (
              <a
                href={domain ? siteHref(domain.hostname, site.link_scheme) : site.url}
                target="_blank"
                rel="noreferrer"
                className={buttonClass("brand", "pill")}
              >
                Open live site ↗
              </a>
            )}
            {!scheduled && (
              <Button
                variant="secondary"
                size="pill"
                requires={{ permission: "websites:delete", on: ["site", siteId] }}
                onClick={() => setConfirming(true)}
              >
                {live ? "Remove site" : "Cancel deployment"}
              </Button>
            )}
          </>
        }
      />

      {scheduled && <PendingDeletionNotice site={site} />}

      <Section variant="tight" className="w-full max-w-[760px]">
        <CardHead
          icon="loading"
          title="Deployment progress"
          aside={<span className="text-xs text-fg-subtle">Checked {formatClock(hosted.dataUpdatedAt)}</span>}
        />
        {steps.map((s) => (
          <div key={s.title} className="flex items-start gap-3.5 py-1.5">
            <div className="pt-px">
              <StepIcon state={s.state} />
            </div>
            <div className="flex min-w-0 flex-1 flex-col gap-0.5">
              <p
                className={cx("text-sm", s.state === "waiting" ? "text-fg-subtle" : "font-semibold text-fg")}
              >
                {s.title}
              </p>
              <p
                className={cx(
                  "text-[12.5px] leading-normal",
                  s.state === "failed" ? "text-[#b8291f]" : "text-fg-subtle",
                )}
              >
                {s.detail}
              </p>
            </div>
            <Badge tone={TONE[s.state]}>{s.label}</Badge>
          </div>
        ))}
        <div className="rounded-xl bg-brand-50 px-3.5 py-3 text-[12.5px] leading-[1.55] text-fg-muted">
          {live
            ? `${address} is live. Upload a new .zip any time from Hosting to replace it.`
            : `Nothing on ${mainAddress ?? "your Weavex website"} changes while this runs. If a step fails, we’ll show what went wrong and you can retry. Your other sites stay as they are.`}
        </div>
        {recheck.error && <ErrorAlert error={recheck.error} />}
        <div className="flex flex-wrap gap-2.5">
          <Button
            unstyled
            type="button"
            className={buttonClass("secondary", "pill")}
            disabled={recheck.isPending || hosted.isFetching}
            aria-busy={recheck.isPending || hosted.isFetching}
            onClick={() => recheck.mutate()}
          >
            {recheck.isPending || (hosted.isFetching && !settled) ? "Checking…" : "Recheck now"}
          </Button>
          {domain?.verification && (
            <Button
              unstyled
              type="button"
              className={buttonClass("secondary", "pill")}
              onClick={() => setShowDns((v) => !v)}
            >
              {showDns ? "Hide DNS record" : "View DNS record"}
            </Button>
          )}
        </div>
        {showDns && domain && <DnsInstructions domain={domain} />}
      </Section>

      <Modal
        open={confirming}
        onClose={() => setConfirming(false)}
        title={live ? `Remove ${site.name}?` : "Cancel this deployment?"}
        footer={
          <>
            <Button
              unstyled
              type="button"
              className={buttonClass("secondary", "pill")}
              onClick={() => setConfirming(false)}
            >
              Keep it
            </Button>
            <Button
              unstyled
              type="button"
              className={buttonClass("brand", "pill")}
              disabled={remove.isPending || grace.loading}
              aria-busy={remove.isPending}
              onClick={() => remove.mutate()}
            >
              {remove.isPending ? "Removing…" : live ? "Remove site" : "Cancel deployment"}
            </Button>
          </>
        }
      >
        {confirming && <DeleteEffect subject={address} />}
        <p className="text-sm text-fg-muted">Your Weavex website and other sites are not affected.</p>
        {remove.error && <ErrorAlert error={remove.error} />}
      </Modal>
    </div>
  );
}
